/* =========================================================================
   Ilene Joy — unified interaction lib
   =========================================================================
   Shared runtime consumed by every page via layout/theme.liquid. Responsible
   for: the top progress bar, opening/closing the cart drawer, rendering the
   drawer contents from /cart.js, handling drawer remove clicks, and
   intercepting the header cart icon so it opens the drawer instead of
   navigating to /cart on every click.

   Global API exposed on window.IJ:
     IJ.openDrawer()       — open + refresh
     IJ.closeDrawer()
     IJ.refreshDrawer()    — re-fetch /cart.js + re-render
     IJ.progress.start()   — show top bar
     IJ.progress.stop()    — hide top bar

   Safety: ALL DOM content insertion goes through document.createElement +
   textContent — never innerHTML with untrusted data. Shopify cart data
   (product titles, prices) is technically trusted but we still avoid
   innerHTML to stay XSS-safe against future content.
   ========================================================================= */

(function () {
  'use strict';

  if (window.IJ && window.IJ.__ready) return;

  var $ = function (sel, root) { return (root || document).querySelector(sel); };
  var $$ = function (sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); };
  /* Money rule shared with the theme (money_without_trailing_zeros) and the builder:
     cents only when non-zero, en-US grouping, never round (2026-09-05). */
  var MONEY = function (cents) { var n = Math.round(Number(cents || 0)); var d = n % 100 === 0 ? 0 : 2; return '$' + (n / 100).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d }); };

  // Remove every child of an element without using innerHTML.
  function clearChildren(el) {
    while (el && el.firstChild) el.removeChild(el.firstChild);
  }

  // ========================================================================
  // Progress bar
  // ========================================================================
  var progressEl = null;
  var progressDepth = 0;

  function ensureProgress() {
    if (progressEl) return progressEl;
    progressEl = document.createElement('div');
    progressEl.className = 'ij-progress';
    progressEl.setAttribute('aria-hidden', 'true');
    document.body.appendChild(progressEl);
    return progressEl;
  }
  function progressStart() {
    ensureProgress();
    progressDepth++;
    progressEl.classList.add('is-active');
  }
  function progressStop() {
    progressDepth = Math.max(0, progressDepth - 1);
    if (progressDepth === 0 && progressEl) progressEl.classList.remove('is-active');
  }

  // ========================================================================
  // Snapshot capture — HYBRID: native canvas renderer (from React bundle
  // via window.__IJ_RENDER_SNAPSHOT__) preferred. Falls back to html2canvas
  // only if the bundle hasn't loaded the global by the time the user hits
  // add-to-cart (rare race: customer clicks before our 538KB bundle parses).
  //
  // WHY NATIVE
  // ----------
  // html2canvas re-implements CSS layout, gets it wrong with `aspect-ratio`
  // and CSS transforms, and produced charms-out-of-position renders that
  // misled the bench team about customer pieces. Native walks the LAID-OUT
  // DOM and paints what's already there. Pixel-perfect.
  //
  // CHROME FILTERING
  // ----------------
  // The flatlay frame contains the chain, MM/lariat overlays, charm
  // markers — AND the SAVE/SHARE action buttons, the PLATE caption bar,
  // the rotate handle that appears on charm-tap, the drag-hint pill, etc.
  // We strip all of those via ignoreSelectors so the keepsake is the
  // composition only.
  // ========================================================================
  var SNAPSHOT_TIMEOUT_MS = 8000;
  var SNAPSHOT_IGNORE_SELECTORS = [
    '.ij__preview-cap',
    '.ij__actions',
    '.ij__action',
    '.ij__frame-pulse',
    '.ij__drag-hint',
    '.ij__charm-rotate-handle',
    '.ij__charm-controls',
    '.ij__model-debug-toggle',
    '.ij__model-overlay-box',
    '.ij__hint-pill',
  ];

  /* Lazy-load html2canvas only if we're about to use the fallback path.
     Keeps the page weight down for the 99% case where the bundle loaded
     and we use the native renderer. */
  var html2canvasReady = null;
  function ensureHtml2Canvas() {
    if (window.html2canvas) return Promise.resolve(window.html2canvas);
    if (html2canvasReady) return html2canvasReady;
    html2canvasReady = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js';
      s.onload = function () { resolve(window.html2canvas); };
      s.onerror = reject;
      document.head.appendChild(s);
    });
    return html2canvasReady;
  }

  function captureViaHtml2Canvas(target) {
    var rect = target.getBoundingClientRect();
    var padX = 48;
    var padTop = 48;
    var padBottom = 96;
    return ensureHtml2Canvas().then(function (h2c) {
      return h2c(target, {
        backgroundColor: '#ffffff',
        useCORS: true,
        allowTaint: false,
        scale: 3,
        logging: false,
        width: rect.width + padX * 2,
        height: rect.height + padTop + padBottom,
        x: -padX,
        y: -padTop,
        ignoreElements: function (node) {
          for (var i = 0; i < SNAPSHOT_IGNORE_SELECTORS.length; i++) {
            try { if (node.matches(SNAPSHOT_IGNORE_SELECTORS[i])) return true; }
            catch (_) { /* invalid selector */ }
          }
          return false;
        },
        onclone: function (_doc, cloned) {
          cloned.style.overflow = 'visible';
          cloned.style.backgroundColor = '#ffffff';
          var pulse = cloned.querySelector('.ij__frame-pulse');
          if (pulse) pulse.style.display = 'none';
        },
      });
    }).then(function (canvas) {
      return new Promise(function (resolve) {
        canvas.toBlob(function (blob) { resolve(blob); }, 'image/jpeg', 0.95);
      });
    });
  }

  /* 2026-11-02: with the On model tab showing, the stage flatlay frame is display:none and
     the cart snapshot came out pure white. If it has no size, show the Flatlay tab for the
     capture and switch back to On model after. (The flatlay copies inside the model frame's
     mirror zone are not the design: never capture those.) */
  function mainFlatlay() {
    var all = document.querySelectorAll('.ij__preview-frame--flatlay');
    for (var i = 0; i < all.length; i++) {
      if (!all[i].closest('.ij__model-mirror-zone')) return all[i];
    }
    return null;
  }
  function visibleFlatlay() {
    var f = mainFlatlay();
    if (!f) return null;
    var r = f.getBoundingClientRect();
    return r.width > 1 && r.height > 1 ? f : null;
  }
  function revealFlatlayTab() {
    var tabs = document.querySelectorAll('#ij-charm-builder .ij__preview-tab');
    var flat = null, model = null;
    for (var i = 0; i < tabs.length; i++) {
      if (!tabs[i].getClientRects().length) continue;
      var tx = (tabs[i].textContent || '').trim().toLowerCase();
      if (tx === 'flatlay' && !tabs[i].classList.contains('is-active')) flat = tabs[i];
      if (tx === 'on model' && tabs[i].classList.contains('is-active')) model = tabs[i];
    }
    if (!flat) return null;
    flat.click();
    return function () { if (model) model.click(); };
  }
  function captureDesignSnapshot() {
    var target = visibleFlatlay();
    if (target) return captureFrom(target);
    var restore = revealFlatlayTab();
    if (!restore) return Promise.resolve(null);
    return new Promise(function (res) { setTimeout(res, 450); }).then(function () {
      var t = visibleFlatlay();
      return t ? captureFrom(t) : null;
    }).then(function (blob) { restore(); return blob; }, function () { restore(); return null; });
  }

  /* Capture the flatlay as a JPEG blob. Returns null on any failure — the
     caller falls back to a no-snapshot cart submission. */
  function captureFrom(target) {

    /* HYBRID PATH 1: native canvas renderer exposed by the React bundle.
       Called the same way as html2canvas but renders pixel-perfectly to
       what's on screen. Uses CORS-refetched images so no canvas tainting. */
    var nativeFn = window.__IJ_RENDER_SNAPSHOT__;
    if (typeof nativeFn === 'function') {
      return nativeFn(target, {
        ignoreSelectors: SNAPSHOT_IGNORE_SELECTORS,
        backgroundColor: '#fbfaf7', /* v0.9.169: light plate tone (was cream) */
        quality: 0.95,
      })
        .then(function (blob) { return blob; })
        .catch(function (err) {
          if (window.console) console.warn('[ij] native snapshot failed, falling back to html2canvas', err);
          return captureViaHtml2Canvas(target).catch(function (err2) {
            if (window.console) console.warn('[ij] html2canvas fallback also failed', err2);
            return null;
          });
        });
    }

    /* HYBRID PATH 2: bundle hasn't loaded yet (or this build is older
       than the one that exposed the global). Use html2canvas. */
    return captureViaHtml2Canvas(target).catch(function (err) {
      if (window.console) console.warn('[ij] snapshot capture failed', err);
      return null;
    });
  }

  /* Wrap captureDesignSnapshot in a hard timeout. html2canvas sometimes
     hangs indefinitely (font-load race, CORS-tainted image, SVG recursion).
     After SNAPSHOT_TIMEOUT_MS we resolve to null so the cart submit can
     fall through without the customer staring at a frozen "Adding to
     cart…" state for 30+ seconds. */
  function captureDesignSnapshotWithTimeout() {
    return new Promise(function (resolve) {
      var settled = false;
      var timer = setTimeout(function () {
        if (settled) return;
        settled = true;
        if (window.console) console.warn('[ij] snapshot capture timed out at ' + SNAPSHOT_TIMEOUT_MS + 'ms; submitting without snapshot');
        resolve({ blob: null, status: 'timeout' });
      }, SNAPSHOT_TIMEOUT_MS);
      captureDesignSnapshot().then(function (blob) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve({ blob: blob, status: blob ? 'ok' : 'capture_failed' });
      });
    });
  }

  // ========================================================================
  // Fetch interceptor — progress bar, snapshot attach, event emit
  // ========================================================================
  var CART_URL_RE = /\/cart\/(add|change|update|clear)\.js/i;
  var ADD_URL_RE = /\/cart\/add\.js/i;
  var origFetch = window.fetch;

  /* Transform a JSON /cart/add.js body into a multipart FormData with the
     snapshot attached to the parent (chain) line item. Shopify's cart
     endpoint accepts the items[N][field] syntax in multipart, stores the
     file blob in Shopify Files CDN, and exposes the URL as a line-item
     property on the order + in /cart.js. Returns null if snapshot missing
     or body shape unexpected, signalling the caller to fall back. */
  function buildAddMultipart(jsonBody, snapshotResult) {
    if (!jsonBody || !Array.isArray(jsonBody.items) || jsonBody.items.length === 0) return null;
    /* Inject _bundle_id here because the charm-builder.liquid injector only
       sees JSON bodies — the moment we switch to FormData below, that
       inline injector no-ops. Stamping every item in the same add-call
       with a shared id keeps the cart-drawer consolidation working. */
    var bundleId = 'bundle-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
    var fd = new FormData();
    /* Find the parent (chain) line up front — we need it for snapshot
       attachment AND for the _snapshot_status property. */
    var parentIdx = jsonBody.items.findIndex(function (i) {
      return i.properties && i.properties._arrangement_json;
    });
    if (parentIdx < 0) parentIdx = 0;
    jsonBody.items.forEach(function (item, i) {
      var props = Object.assign({}, item.properties || {});
      if (!props._bundle_id) props._bundle_id = bundleId;
      /* Stamp every line item with the snapshot status. Fulfillment queries
         by _bundle_id → if status is 'ok' they pull the _design file;
         otherwise they know the customer's design wasn't captured and they
         should cross-reference the _arrangement_json to reconstruct the
         piece, or reach out to the customer for a re-submission. */
      if (i === parentIdx && snapshotResult) {
        props._snapshot_status = snapshotResult.status;
      }
      fd.append('items[' + i + '][id]', String(item.id));
      fd.append('items[' + i + '][quantity]', String(item.quantity || 1));
      Object.keys(props).forEach(function (k) {
        fd.append('items[' + i + '][properties][' + k + ']', String(props[k]));
      });
    });
    if (snapshotResult && snapshotResult.blob) {
      /* Attach to the parent (chain) line item — the one carrying
         _arrangement_json. Underscore-prefixed property so it's HIDDEN from
         the customer's order confirmation email (Shopify convention) while
         staying visible to the atelier team on the order admin. The cart
         template still reads this via item.properties['_design'] to render
         the thumbnail. */
      fd.append('items[' + parentIdx + '][properties][_design]', snapshotResult.blob, 'design-preview.jpg');
    }
    return fd;
  }

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var isCartMut = CART_URL_RE.test(url);
    var isAdd = ADD_URL_RE.test(url);

    if (isAdd && init && typeof init.body === 'string') {
      /* Snapshot path: capture the flatlay then POST multipart with file.
         If snapshot fails we fall through to the vanilla JSON path so the
         purchase never gets blocked on image-capture infrastructure. */
      try {
        var jsonBody = JSON.parse(init.body);
        progressStart();
        return captureDesignSnapshotWithTimeout().then(function (snapshotResult) {
          var fd = buildAddMultipart(jsonBody, snapshotResult);
          if (!fd) return origFetch(input, init); // shape unexpected — fall through
          /* Don't copy Content-Type from init — browser sets it with the
             right multipart boundary when body is FormData. */
          var newInit = { method: 'POST', body: fd, credentials: init.credentials || 'same-origin' };
          return origFetch(url, newInit);
        }).then(function (res) {
          try {
            res.clone().json().then(function (data) {
              window.dispatchEvent(new CustomEvent('ij:cart-mutated', { detail: { url: url, data: data } }));
              window.dispatchEvent(new CustomEvent('ij:cart-added', { detail: { data: data } }));
            }).catch(function () {});
          } catch (_) {}
          return res;
        }).finally(function () { progressStop(); });
      } catch (_) {
        /* Body wasn't valid JSON — let it through unchanged. */
      }
    }

    if (isCartMut) progressStart();
    var p = origFetch.apply(this, arguments);
    if (isCartMut) {
      p.then(function (res) {
        try {
          var clone = res.clone();
          clone.json().then(function (data) {
            window.dispatchEvent(new CustomEvent('ij:cart-mutated', { detail: { url: url, data: data } }));
            if (isAdd) {
              window.dispatchEvent(new CustomEvent('ij:cart-added', { detail: { data: data } }));
            }
          }).catch(function () {});
        } catch (_) {}
      }).finally(function () { progressStop(); });
    }
    return p;
  };

  // ========================================================================
  // Ripple tap feedback
  // ========================================================================
  function addRipple(el) {
    if (!el || el.__ijTap) return;
    el.__ijTap = true;
    el.classList.add('ij-tap');
    el.addEventListener('click', function () {
      el.classList.remove('is-pressed');
      void el.offsetWidth;
      el.classList.add('is-pressed');
      setTimeout(function () { el.classList.remove('is-pressed'); }, 400);
    });
  }

  function wireCartButtons() {
    $$('.nav-cart a, [data-custom-cart-remove], .quantity-minus, .quantity-plus, .cart__checkout, [name="checkout"], .ij-drawer-checkout, .ij-drawer-bundle-remove').forEach(addRipple);
  }

  // ========================================================================
  // Cart drawer
  // ========================================================================
  var drawerEl = null;
  var scrimEl = null;
  var drawerBodyEl = null;
  var drawerFootEl = null;
  var drawerCountEl = null;
  /* v0.9.196: last cart snapshot seen by updateCartCounts — reapplied after a SPA swap (see ij:navigated). */
  var lastCart = null;

  function mountDrawer() {
    if (drawerEl) return;
    drawerEl = $('.ij-cart-drawer');
    scrimEl = $('.ij-drawer-scrim');
    if (!drawerEl || !scrimEl) return;
    drawerBodyEl = $('.ij-drawer-body', drawerEl);
    drawerFootEl = $('.ij-drawer-foot', drawerEl);
    drawerCountEl = $('.ij-drawer-count', drawerEl);

    scrimEl.addEventListener('click', closeDrawer);
    $('.ij-drawer-close', drawerEl).addEventListener('click', closeDrawer);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawerEl.classList.contains('is-open')) closeDrawer();
    });
  }

  function renderLoading() {
    if (!drawerBodyEl) return;
    clearChildren(drawerBodyEl);
    var spinner = document.createElement('div');
    spinner.className = 'ij-drawer-empty';
    var p = document.createElement('p');
    p.textContent = 'Loading…';
    p.style.color = 'var(--ij-ink-3)';
    spinner.appendChild(p);
    drawerBodyEl.appendChild(spinner);
    if (drawerFootEl) drawerFootEl.style.display = 'none';
  }

  function openDrawer() {
    mountDrawer();
    if (!drawerEl) return;
    // Clear stale content from any previous render BEFORE showing the panel,
    // then kick off the refresh. Prevents the "old cart total flashes for 1s"
    // race where the panel animates in showing the previous cart state.
    renderLoading();
    drawerEl.classList.add('is-open');
    scrimEl.classList.add('is-open');
    document.documentElement.classList.add('ij-drawer-open');
    document.body.classList.add('ij-drawer-open');
    refreshDrawer();
  }
  function closeDrawer() {
    if (!drawerEl) return;
    drawerEl.classList.remove('is-open');
    scrimEl.classList.remove('is-open');
    document.documentElement.classList.remove('ij-drawer-open');
    document.body.classList.remove('ij-drawer-open');
  }

  function renderEmpty() {
    clearChildren(drawerBodyEl);
    var wrap = document.createElement('div');
    wrap.className = 'ij-drawer-empty';
    var p = document.createElement('p');
    p.textContent = 'Your bag is empty.';
    var a = document.createElement('a');
    a.href = '/products/box';
    a.textContent = 'Design a necklace';
    wrap.appendChild(p);
    wrap.appendChild(a);
    /* 2026-09-07 funnel audit: one route back was a dead end for bracelet / charm /
       ready-to-ship shoppers. */
    var more = document.createElement('p');
    more.className = 'ij-drawer-empty-links';
    [['/products/box-chain-bracelet', 'Design a bracelet'], ['/collections/charms', 'Shop charms'], ['/collections/ready-to-ship', 'Ready to ship']].forEach(function (l, i) {
      if (i) more.appendChild(document.createTextNode(' \u00b7 '));
      var x = document.createElement('a'); x.href = l[0]; x.textContent = l[1]; more.appendChild(x);
    });
    wrap.appendChild(more);
    drawerBodyEl.appendChild(wrap);
    if (drawerFootEl) drawerFootEl.style.display = 'none';
    if (drawerCountEl) drawerCountEl.textContent = '';
  }

  /**
   * WP-B 2026-10-10: THE cart item count, used by the header badge, the drawer title and
   * (mirrored in Liquid) the cart page subtitle in templates/cart.liquid. Keep the two in step.
   *
   * Rule: quantity sum of the pieces the customer sees. A designed piece (lines sharing
   * `_bundle_id` whose bundle has a parent line carrying `_arrangement_json`) counts as its
   * parent line's quantity; the charms / connector / lariat lines inside it count 0. Every
   * other line counts its own quantity. So 1 bracelet design with 2 charms + 1 loose charm
   * x2 = 3 items, never "1" (old design count ignored quantity) and never "5" (raw
   * cart.item_count). A bundle with no parent line (bad data) is not consolidated on the
   * cart page, so its lines count individually here too.
   *
   * Falls back to cart.item_count when the items array is missing.
   */
  function isDesignParent(item) {
    var v = item && item.properties && item.properties._arrangement_json;
    return v != null && String(v).trim() !== '';
  }
  function cartItemCount(cart) {
    if (!cart || !Array.isArray(cart.items)) {
      return cart && typeof cart.item_count === 'number' ? cart.item_count : 0;
    }
    var designed = {};
    cart.items.forEach(function (item) {
      var bid = item.properties && item.properties._bundle_id;
      if (bid && isDesignParent(item)) designed[bid] = true;
    });
    var n = 0;
    cart.items.forEach(function (item) {
      var bid = item.properties && item.properties._bundle_id;
      if (bid && designed[bid] && !isDesignParent(item)) return;
      n += Math.max(0, parseInt(item.quantity, 10) || 0);
    });
    return n;
  }

  /**
   * Update every cart-count display in the page.
   *
   * The header cart icon uses `[data-cart-render="item_count"]` (legacy
   * CartJS hook in vendor.js, normally only updated when a request goes
   * through CartJS's queue). Our drawer uses `origFetch` directly to
   * bypass CartJS — so we update both spots manually.
   *
   * Also updates `data-cart-render="design_count"` if any future template
   * wants the design-aware count specifically.
   */
  function updateCartCounts(cart) {
    lastCart = cart;
    var designs = cartItemCount(cart);
    /* External header icon — was showing raw item_count (e.g. "6"
       for one bundle). Now the one shared count (cartItemCount). */
    var headerCountEls = document.querySelectorAll('[data-cart-render="item_count"]');
    for (var i = 0; i < headerCountEls.length; i++) {
      headerCountEls[i].textContent = String(designs);
      /* Hide the badge entirely when cart is empty — nicer than
         showing a "0" that catches the eye. */
      headerCountEls[i].style.visibility = designs === 0 ? 'hidden' : '';
    }
    /* Future-proof: any new templates can use design_count semantically. */
    var designCountEls = document.querySelectorAll('[data-cart-render="design_count"]');
    for (var j = 0; j < designCountEls.length; j++) {
      designCountEls[j].textContent = String(designs);
    }
    /* Drawer header — same design count, with units. */
    if (drawerCountEl) {
      drawerCountEl.textContent = designs + (designs === 1 ? ' item' : ' items');
    }
  }

  function renderDrawer(cart) {
    if (!drawerBodyEl) return;
    clearChildren(drawerBodyEl);

    /* Update icon counts FIRST so the header is in sync even if the
       drawer body render below errors out. */
    updateCartCounts(cart);

    if (!cart.items || !cart.items.length) { renderEmpty(); return; }

    var groups = {};
    var loners = [];
    cart.items.forEach(function (item) {
      var bid = item.properties && item.properties._bundle_id;
      if (bid) { (groups[bid] = groups[bid] || []).push(item); }
      else { loners.push(item); }
    });

    Object.keys(groups).forEach(function (bid) {
      var items = groups[bid];
      /* WP-B: same rule as the cart page consolidation and cartItemCount — a bundle with no
         `_arrangement_json` parent is not a designed piece; show its lines on their own. */
      var parent = items.find(isDesignParent);
      if (!parent) {
        items.forEach(function (item) { drawerBodyEl.appendChild(renderBundle(item, [], [item])); });
        return;
      }
      var children = items.filter(function (i) { return i !== parent; });
      drawerBodyEl.appendChild(renderBundle(parent, children, items));
    });
    loners.forEach(function (item) {
      drawerBodyEl.appendChild(renderBundle(item, [], [item]));
    });

    if (drawerFootEl) {
      drawerFootEl.style.display = '';
      var subtotalEl = $('.ij-drawer-subtotal .amount', drawerFootEl);
      if (subtotalEl) subtotalEl.textContent = MONEY(cart.total_price);
    }
    refreshInstallments(cart);
    try { if (window.IJAttr && IJAttr.sync) IJAttr.sync(); } catch (e) {}
  }

  /* WP-B: line label for a charm / part inside a designed piece. Same text as the cart page's
     "Designed piece - includes" rows: "Charm - " prefix dropped (stop-gap for #11 until the
     product titles change), variant in brackets, "Magic Middle" dropped from the variant. */
  function childLabel(c) {
    var t = String(c.product_title || c.title || '').replace(/^Charm\s*[-\u2013]\s*/i, '').replace(/\s*-\s*$/, '').trim();
    var v = String(c.variant_title || '').trim();
    if (v && v !== 'Default Title') {
      v = v.replace(/\bMagic\s*Middle\b/gi, '').replace(/\s*\/\s*/g, ' / ').replace(/^\s*\/\s*|\s*\/\s*$/g, '').replace(/\s+/g, ' ').trim();
    }
    return v && v !== 'Default Title' ? t + ' (' + v + ')' : t;
  }

  function visibleLineProps(item) {
    var out = [];
    var props = item && item.properties;
    if (!props || typeof props !== 'object') return out;
    Object.keys(props).forEach(function (k) {
      if (!k || k.charAt(0) === '_') return;
      var v = props[k];
      if (v == null) return;
      v = String(v);
      if (!v.trim()) return;
      out.push([k, v]);
    });
    return out;
  }

  function renderLineProps(item) {
    var list = visibleLineProps(item);
    if (!list.length) return null;
    ensureDrawerPropsCss();
    var dl = document.createElement('dl');
    dl.className = 'ij-drawer-props';
    list.forEach(function (kv) {
      var row = document.createElement('div');
      var dt = document.createElement('dt');
      dt.textContent = kv[0];
      var dd = document.createElement('dd');
      if (kv[1].indexOf('/uploads/') !== -1 && /^https:\/\/[^\s"'<>\\]+$/.test(kv[1])) {
        var a = document.createElement('a');
        a.href = kv[1];
        a.target = '_blank';
        a.rel = 'noopener';
        a.textContent = 'View file';
        dd.appendChild(a);
      } else {
        dd.textContent = kv[1];
      }
      row.appendChild(dt);
      row.appendChild(dd);
      dl.appendChild(row);
    });
    return dl;
  }

  /* 2026-10-11: Saved designs (assets/ij-saved-designs-v1.js) - loaded once, next to this file, when the drawer shows a designed
     piece. Its document-level click handler takes [data-ij-save-design] buttons; until it has loaded the button's own handler
     loads it and replays the click. */
  var savedLoading = null;
  function loadSavedDesigns(cb) {
    if (window.IJSaved) { if (cb) cb(); return; }
    if (!savedLoading) {
      savedLoading = [];
      /* the versioned URL (asset_url, ?v=) from snippets/ij-saved-email-form.liquid; next to this file as a fallback */
      var home = document.getElementById('ij-saved-email-home');
      var me = document.querySelector('script[src*="/ij-ux.js"]');
      var src = (home && home.getAttribute('data-src')) || (me && me.src ? me.src.replace(/ij-ux\.js(\?.*)?$/, 'ij-saved-designs-v1.js') : '');
      if (!src || (me && src === me.src)) { savedLoading = null; return; }
      var sc = document.createElement('script');
      sc.src = src; sc.async = true;
      sc.onload = function () { var q = savedLoading || []; savedLoading = null; q.forEach(function (f) { try { f(); } catch (e) {} }); };
      sc.onerror = function () { savedLoading = null; };
      document.head.appendChild(sc);
    }
    if (cb && savedLoading) savedLoading.push(cb);
  }
  function saveDesignButton(editUrl, thumb, parent) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'ij-save-chip ij-drawer-bundle-save';
    b.setAttribute('data-ij-save-design', '');
    b.setAttribute('data-source', 'cart');
    b.setAttribute('data-url', editUrl);
    if (thumb && /^https:\/\/[^\s"'<>\\]+$/.test(thumb)) b.setAttribute('data-thumb', thumb);
    var handle = String(parent.handle || parent.url || '');
    b.setAttribute('data-kind', /bracelet/i.test(handle + ' ' + editUrl) ? 'bracelet' : 'necklace');
    b.setAttribute('data-spec', String(parent.product_title || '').replace(/^Charm\s*[-\u2013]\s*/i, '') + (parent.variant_title && parent.variant_title !== 'Default Title' ? ' \u00b7 ' + String(parent.variant_title).replace(/\s*\/\s*/g, ' \u00b7 ') : ''));
    var NS = 'http://www.w3.org/2000/svg', svg = document.createElementNS(NS, 'svg'), path = document.createElementNS(NS, 'path');
    svg.setAttribute('viewBox', '0 0 16 16'); svg.setAttribute('width', '14'); svg.setAttribute('height', '14'); svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor'); svg.setAttribute('stroke-width', '1.3'); svg.setAttribute('stroke-linejoin', 'round'); svg.setAttribute('aria-hidden', 'true');
    path.setAttribute('d', 'M4 2.5h8v11l-4-3-4 3z'); svg.appendChild(path); b.appendChild(svg);
    var lab = document.createElement('span'); lab.setAttribute('data-ij-save-label', ''); lab.textContent = 'Save design'; b.appendChild(lab);
    /* only reached while the module is not loaded yet (once loaded it handles the click first and stops it) */
    b.addEventListener('click', function (e) {
      if (window.IJSaved) return;
      e.preventDefault(); e.stopPropagation();
      loadSavedDesigns(function () { b.click(); });
    });
    if (window.IJSaved && window.IJSaved.sync) setTimeout(window.IJSaved.sync, 0);
    return b;
  }

  /* Styles live here (not in ij-ux.css) so the drawer property block ships as one file; injected once. */
  function ensureDrawerPropsCss() {
    if (document.getElementById('ij-drawer-props-css')) return;
    var st = document.createElement('style');
    st.id = 'ij-drawer-props-css';
    st.textContent =
      '.ij-drawer-props{margin:6px 0 0;padding:0;display:flex;flex-direction:column;gap:2px;font-size:11.5px;line-height:1.45;color:var(--ij-ink-3)}' +
      '.ij-drawer-props>div{display:grid;grid-template-columns:104px 1fr;gap:10px}' +
      '.ij-cart-drawer .ij-drawer-props dt{font-weight:500;margin:0;color:var(--ij-ink-3,#707070)!important}' +
      '.ij-drawer-props--child>div{grid-template-columns:92px 1fr}' +
      '.ij-drawer-props dd{margin:0;color:var(--ij-ink-2);word-break:break-word}' +
      '.ij-drawer-bundle-children>dl.ij-drawer-props--child{display:flex;margin:0 0 4px 12px;font-size:11px}';
    (document.head || document.documentElement).appendChild(st);
  }

  /* 2026-10-19 drawer upsells (see renderBundle). */
  var PAIR_FAMILIES = ['box', 'circle', 'oval', 'paperclip', 'rolo', 'ball'];
  function trackUpsell(action, type) {
    try { if (window.IJTrack && IJTrack.upsell) IJTrack.upsell(action, type, 0, 'drawer'); } catch (e) {}
  }
  function cartHasKind(kind) {
    var items = (lastCart && lastCart.items) || [];
    return items.some(function (i) {
      if (!(i.properties && i.properties._arrangement_json)) return false;
      var b = /-chain-bracelet$/.test(String(i.handle || ''));
      return kind === 'bracelet' ? b : !b;
    });
  }
  function pairLink(parent, editUrl) {
    var handle = String(parent.handle || '');
    var isBracelet = /-chain-bracelet$/.test(handle);
    var family = isBracelet ? handle.replace(/-chain-bracelet$/, '') : handle;
    if (PAIR_FAMILIES.indexOf(family) < 0) return null;
    var want = isBracelet ? 'necklace' : 'bracelet';
    if (cartHasKind(want)) return null;
    var url = parent.properties && parent.properties._pair_url;
    if (typeof url !== 'string' || !/^\/products\/[a-z0-9-]+(\?[^\s"'<>]{0,8000})?$/i.test(url)) {
      url = '/products/' + (isBracelet ? family : family + '-chain-bracelet');
    }
    var a = document.createElement('a');
    a.href = url + (url.indexOf('?') < 0 ? '?' : '&') + 'ij_from=drawer_complete_set';
    a.textContent = isBracelet ? 'Design the matching necklace' : 'Design the matching bracelet';
    a.setAttribute('data-ij-upsell', 'drawer_complete_set');
    return a;
  }
  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest ? e.target.closest('.ij-cart-drawer [data-ij-upsell]') : null;
    if (a) trackUpsell('click', a.getAttribute('data-ij-upsell'));
  }, true);
  /* After an add on a builder page opened from the drawer: credit the upsell and, for Edit / Add a charm, remove the piece
     that was being edited (the new add is its replacement). Marker set by snippets/ij-attribution.liquid (ij_from/ij_replace). */
  function handleEditMarker(data) {
    var m = null;
    try { m = JSON.parse(sessionStorage.getItem('ij_edit') || 'null'); } catch (e) { m = null; }
    if (!m || m.path !== location.pathname || Date.now() - (m.at || 0) > 3 * 3600 * 1000) return;
    try { sessionStorage.removeItem('ij_edit'); } catch (e) {}
    var items = (data && data.items) || [];
    var newBid = items[0] && items[0].properties && items[0].properties._bundle_id;
    if (m.from === 'drawer_add_charm' || m.from === 'drawer_complete_set') trackUpsell('accept', m.from);
    if (!m.bid || !newBid || newBid === m.bid) return;
    origFetch('/cart.js', { headers: { 'Accept': 'application/json' }, cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var updates = {}, n = 0;
        (cart.items || []).forEach(function (it) { if (it.properties && it.properties._bundle_id === m.bid) { updates[it.key] = 0; n++; } });
        if (!n) return null;
        return origFetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ updates: updates }) })
          .then(function () { refreshDrawer(); });
      })
      .catch(function () { /* the customer still has both pieces; nothing lost */ });
  }
  /* Shop Pay Installments line in the drawer: Shopify's own banner (payment_terms) for the current cart total, rendered by
     sections/ij-installments.liquid through the Section Rendering API. Renders nothing when Shopify has no offer. */
  var instTotal = -1;
  function refreshInstallments(cart) {
    var slot = drawerEl && $('[data-ij-drawer-inst]', drawerEl);
    if (!slot || !cart) return;
    if (!cart.item_count) { clearChildren(slot); instTotal = -1; return; }
    if (cart.total_price === instTotal) return;
    instTotal = cart.total_price;
    origFetch('/cart?section_id=ij-installments', { headers: { 'Accept': 'text/html' }, cache: 'no-store' })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var node = doc.querySelector('[data-ij-inst]');
        clearChildren(slot);
        if (node && node.querySelector('shopify-payment-terms')) slot.appendChild(document.importNode(node, true));
      })
      .catch(function () { /* no line is fine */ });
  }

  function renderBundle(parent, children, allItems) {
    var row = document.createElement('div');
    row.className = 'ij-drawer-bundle';
    row.dataset.parentKey = parent.key;

    var img = document.createElement('img');
    img.className = 'ij-drawer-thumb';
    /* Prefer the customer's uploaded design snapshot over the product's
       default featured image — the whole point of this cart is the piece
       they designed. The snapshot is attached as a line-item file property
       named "Design" by our /cart/add.js interceptor; Shopify replaces the
       property value with the CDN URL after upload. Falls back gracefully
       if the snapshot wasn't captured (old sessions, capture errors). */
    // v0.5.7: key is `_design` now (underscore hides from order confirmation
    // emails). Fall back to legacy `Design` for carts that persisted before
    // the rename — customers with in-flight carts keep working.
    var designUrl = parent.properties && (parent.properties['_design'] || parent.properties['Design']);
    /* v0.9.216 (A23): the builder stamps `_preview` = the theme-asset plate of the ordered chain x size x metal (+ clasp)
       so a rose / white piece no longer shows the product's yellow cover. https theme assets only. */
    var previewUrl = parent.properties && parent.properties._preview;
    if (typeof previewUrl !== 'string' || !/^https:\/\/[^\s"'<>\\]+\/assets\/[^\s"'<>\\]+$/.test(previewUrl)) previewUrl = '';
    img.src = designUrl || previewUrl || parent.image || '';
    img.alt = parent.title || '';
    img.loading = 'lazy';
    row.appendChild(img);

    var info = document.createElement('div');
    info.className = 'ij-drawer-bundle-info';

    var title = document.createElement('h3');
    title.className = 'ij-drawer-bundle-title';
    /* WP-B: "Charm - " title prefix dropped, as on the cart page (stop-gap for #11). */
    title.textContent = String(parent.product_title || parent.title || '').replace(/^Charm\s*[-\u2013]\s*/i, '');
    info.appendChild(title);

    if (parent.variant_title && parent.variant_title !== 'Default Title') {
      var variant = document.createElement('div');
      variant.className = 'ij-drawer-bundle-variant';
      /* WP-B: same wording as the cart page ("Size Jumbo · 14K · Rose"). */
      var opt1 = (parent.options_with_values && parent.options_with_values[0] && parent.options_with_values[0].name) || '';
      variant.textContent = (/size/i.test(opt1) ? 'Size ' : '') + String(parent.variant_title).replace(/\s*\/\s*/g, ' \u00b7 ');
      info.appendChild(variant);
    }

    /* 'Edit design' (2026-09-05): the builder stamps `_edit_url` (path + full design
       query) on the chain parent at add time. Only builder pieces carry it — ordinary
       products get no link. Guard: same-origin /products/ path only, so a crafted
       property can never become javascript:; href is set as a property, never via
       innerHTML. */
    var editUrl = parent.properties && parent.properties._edit_url;
    if (typeof editUrl === 'string' && /^\/products\/[a-z0-9-]+\?[^\s"'<>]{1,8000}$/i.test(editUrl)) {
      /* 2026-10-19 (recall-backups/2026-10-19-funnels): Edit design REPLACES this piece. ij_replace = this bundle; after the
         next add on that builder page the old lines are removed (handleEditMarker). ij_from credits drawer upsells. */
      var bid = parent.properties && parent.properties._bundle_id;
      /* bundle ids are UUIDs from the builder (lineItemBuilder) or "bundle-<ms>-<rand>" from the section's injector */
      var rep = (typeof bid === 'string' && /^[a-z0-9][a-z0-9-]{7,63}$/i.test(bid)) ? '&ij_replace=' + encodeURIComponent(bid) : '';
      var edit = document.createElement('a');
      edit.className = 'ij-drawer-bundle-edit';
      edit.href = editUrl + '&ij_from=drawer_edit' + rep;
      edit.textContent = 'Edit design';
      /* 2026-10-11 (Ilene: "the save should be very visible"): Save design next to Edit design - keeps the design (its link +
         the picture above) in Saved designs. The list and its sheet are assets/ij-saved-designs-v1.js (loaded here on demand;
         the builder bundle carries the same file). Rollback: recall-backups/2026-10-11-save-design/theme/ROLLBACK_theme.sh */
      var acts = document.createElement('div');
      acts.className = 'ij-drawer-bundle-design';
      acts.style.cssText = 'display:flex;flex-wrap:wrap;align-items:center;gap:8px 16px;margin:4px 0 6px';
      acts.appendChild(edit);
      acts.appendChild(saveDesignButton(editUrl, img.src, parent));
      info.appendChild(acts);
      loadSavedDesigns();
      /* 2026-10-19 truthful upsells at the decision moment: nothing pre-ticked, nothing added for the customer. "Add a charm"
         reopens this exact design at the Charms step (step=charms is read by builder v0.9.261; older builds open as Edit).
         "Complete the set" opens the matching bracelet / necklace builder (_pair_url from v0.9.261 carries the charms;
         otherwise the same chain family). Clicks/accepts: GA4 + Meta via window.IJTrack, `_upsell` cart attribute. */
      var ups = document.createElement('div');
      ups.className = 'ij-drawer-upsell';
      var addC = document.createElement('a');
      addC.href = editUrl + '&step=charms&ij_from=drawer_add_charm' + rep;
      addC.textContent = 'Add a charm to this design';
      addC.setAttribute('data-ij-upsell', 'drawer_add_charm');
      ups.appendChild(addC);
      var pair = pairLink(parent, editUrl);
      if (pair) ups.appendChild(pair);
      info.appendChild(ups);
      trackUpsell('view', 'drawer_add_charm');
      if (pair) trackUpsell('view', 'drawer_complete_set');
    }

    /* WP-B: the customer-visible line properties (Wrist size, Connector, XL notes...), same set
       and order as the cart page: every property whose name does not start with "_" and whose
       value is not blank, in the order Shopify stores them. */
    var parentProps = renderLineProps(parent);
    if (parentProps) info.appendChild(parentProps);

    if (children.length) {
      var kids = document.createElement('div');
      kids.className = 'ij-drawer-bundle-children';
      children.forEach(function (c) {
        var line = document.createElement('div');
        var label = document.createElement('span');
        label.textContent = '+ ' + childLabel(c);
        var price = document.createElement('span');
        price.textContent = MONEY(c.final_line_price);
        line.appendChild(label);
        line.appendChild(price);
        kids.appendChild(line);
        var childProps = renderLineProps(c);
        if (childProps) { childProps.classList.add('ij-drawer-props--child'); kids.appendChild(childProps); }
      });
      info.appendChild(kids);
    }

    var totalCents = allItems.reduce(function (acc, i) { return acc + i.final_line_price; }, 0);
    var totalRow = document.createElement('div');
    totalRow.className = 'ij-drawer-bundle-total';
    var totalLabel = document.createElement('span');
    totalLabel.textContent = MONEY(totalCents);
    var removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'ij-drawer-bundle-remove';
    removeBtn.textContent = 'Remove';
    removeBtn.addEventListener('click', function () {
      removeBundle(removeBtn, allItems);
    });
    addRipple(removeBtn);
    totalRow.appendChild(totalLabel);
    totalRow.appendChild(removeBtn);
    info.appendChild(totalRow);

    row.appendChild(info);
    return row;
  }

  /**
   * Optimistic bundle remove.
   *
   * The OLD flow waited for /cart/update.js to round-trip (which under
   * Shopify cart-app cascades can take 30-60s for an 11-item bundle)
   * and only then updated the drawer. Customers stared at a "Remove"
   * button for a minute. Worse, refreshDrawer was called twice — once
   * via the ij:cart-mutated event listener, once via the explicit .then.
   *
   * NEW flow: optimistic.
   *   1. Animate the bundle row out IMMEDIATELY (fade + slide).
   *   2. Update the drawer subtotal client-side using the items we know
   *      we're removing (so the total flashes to the correct new value).
   *   3. Fire the /cart/update.js call in the background.
   *   4. On success: do nothing — the optimistic state IS correct, and the
   *      ij:cart-mutated event the fetch interceptor dispatches will
   *      trigger a refreshDrawer that reconciles any other drift.
   *   5. On error: rollback — re-insert the bundle row + restore subtotal +
   *      toast message. (User decides UX of the rollback toast — see TODO.)
   *
   * This makes the perceived removal latency ~0ms regardless of how
   * slow the cart endpoint is.
   */
  function removeBundle(btn, items) {
    if (btn.classList.contains('ij-btn-loading')) return;
    btn.classList.add('ij-btn-loading');

    /* 1. OPTIMISTIC DOM REMOVAL — capture the bundle row + the next
          sibling so we know where to restore on rollback. */
    var bundleRow = btn.closest('.ij-drawer-bundle');
    /* v0.9.196 (audit row 10): if this is the LAST design in the bag, the drawer must go straight to the
       empty state — not sit on "Subtotal $0.00 · Checkout · View full cart" under an empty body while
       /cart/update.js round-trips (up to a minute on a big bundle). */
    if (bundleRow) bundleRow.setAttribute('data-ij-removing', '1');
    var removingLast = !!(drawerBodyEl && bundleRow && !drawerBodyEl.querySelector('.ij-drawer-bundle:not([data-ij-removing])'));
    if (removingLast && drawerFootEl) drawerFootEl.style.display = 'none';
    var nextSibling = bundleRow ? bundleRow.nextSibling : null;
    var parentEl = bundleRow ? bundleRow.parentNode : null;
    if (bundleRow) {
      bundleRow.style.transition = 'opacity 220ms ease, transform 220ms ease, max-height 280ms ease, margin 280ms ease, padding 280ms ease';
      bundleRow.style.maxHeight = bundleRow.offsetHeight + 'px'; // pin for transition
      requestAnimationFrame(function () {
        bundleRow.style.opacity = '0';
        bundleRow.style.transform = 'translateX(8px)';
        bundleRow.style.maxHeight = '0px';
        bundleRow.style.marginTop = '0';
        bundleRow.style.marginBottom = '0';
        bundleRow.style.paddingTop = '0';
        bundleRow.style.paddingBottom = '0';
      });
      setTimeout(function () {
        if (bundleRow.parentNode) bundleRow.parentNode.removeChild(bundleRow);
        if (removingLast && drawerBodyEl && !drawerBodyEl.querySelector('.ij-drawer-bundle')) renderEmpty();
      }, 300);
    }

    /* 2. OPTIMISTIC SUBTOTAL — subtract the bundle's total from the
          drawer subtotal so the total reflects the new state instantly. */
    var removedTotal = items.reduce(function (acc, i) { return acc + (i.final_line_price || 0); }, 0);
    var subtotalEl = drawerFootEl ? $('.ij-drawer-subtotal .amount', drawerFootEl) : null;
    var prevSubtotalText = subtotalEl ? subtotalEl.textContent : null;
    if (subtotalEl) {
      var mm = (prevSubtotalText || '').match(/\$([\d,]+)(?:\.(\d{2}))?/);
      var currentCents = mm ? parseInt(mm[1].replace(/,/g, ''), 10) * 100 + (mm[2] ? parseInt(mm[2], 10) : 0) : NaN;
      if (!isNaN(currentCents)) {
        subtotalEl.textContent = MONEY(Math.max(0, currentCents - removedTotal));
      }
    }
    /* Optimistic item count: a bundle = 1 design. Decrement BOTH the
       drawer header AND the external cart-icon badge. We don't know the
       new total raw cart count without a /cart.js fetch, but we know
       this remove subtracts exactly ONE design. */
    var prevCountText = drawerCountEl ? drawerCountEl.textContent : null;
    var prevHeaderCounts = [];
    var headerCountEls = document.querySelectorAll('[data-cart-render="item_count"]');
    for (var hi = 0; hi < headerCountEls.length; hi++) {
      prevHeaderCounts.push({ el: headerCountEls[hi], text: headerCountEls[hi].textContent, vis: headerCountEls[hi].style.visibility });
    }
    /* WP-B: subtract this piece's share of the shared count (its quantity), not a flat 1. */
    var removedCount = cartItemCount({ items: items });
    if (drawerCountEl && prevCountText) {
      var prevCount = parseInt(prevCountText, 10);
      if (!isNaN(prevCount)) {
        var newCount = Math.max(0, prevCount - removedCount);
        drawerCountEl.textContent = newCount + (newCount === 1 ? ' item' : ' items');
      }
    }
    for (var hi2 = 0; hi2 < headerCountEls.length; hi2++) {
      var prev = parseInt(headerCountEls[hi2].textContent, 10);
      if (!isNaN(prev)) {
        var nextN = Math.max(0, prev - removedCount);
        headerCountEls[hi2].textContent = String(nextN);
        headerCountEls[hi2].style.visibility = nextN === 0 ? 'hidden' : '';
      }
    }

    /* 3. BACKGROUND API CALL — fire-and-forget from the user's
          perspective. The fetch interceptor still fires ij:cart-mutated
          on success, which triggers a refreshDrawer in the listener
          below (line ~575). That refresh reconciles any drift between
          our optimistic state and Shopify's authoritative state.
          We use `origFetch` directly to bypass the wrapper's progress
          bar — we already removed the row, no need to flash a loading
          state. */
    var updates = {};
    items.forEach(function (i) { updates[i.key] = 0; });
    origFetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates }),
    }).then(function (res) {
      if (!res.ok) throw new Error('cart/update.js status ' + res.status);
      /* Manually fire ij:cart-mutated so the listener refreshes the
         drawer to reconcile (origFetch bypasses the interceptor's
         event dispatch). The refresh re-fetches /cart.js and calls
         renderDrawer → updateCartCounts → header icon syncs to truth. */
      window.dispatchEvent(new CustomEvent('ij:cart-mutated', { detail: { url: '/cart/update.js' } }));
    }).catch(function (err) {
      /* 4. ROLLBACK — the API call failed. We need to put things back. */
      console.error('[ij] bundle remove failed', err);
      handleRemoveBundleFailure(bundleRow, parentEl, nextSibling, prevSubtotalText, prevCountText, subtotalEl, prevHeaderCounts);
      /* v0.9.196: we had already switched to the empty state — rebuild the drawer from the real cart. */
      if (removingLast) refreshDrawer();
    });
  }

  /**
   * Restore the drawer state when a bundle remove API call fails.
   *
   * Parameters:
   *   bundleRow:        the DOM node we removed (already detached)
   *   parentEl:         the drawer container we removed it from
   *   nextSibling:      the node it was sitting before (null = was at end)
   *   prevSubtotalText: the cart subtotal before the optimistic update
   *   prevCountText:    the item count text before the optimistic update
   *   subtotalEl:       the subtotal DOM node to restore (may be null)
   *
   * TODO[user]: Decide the rollback UX (5-10 lines). This is a meaningful
   * UX choice — what should the customer see when a remove fails?
   *
   * Options to consider:
   *   A. Silent restore — re-insert the row, restore totals, show nothing.
   *      User sees the bundle "come back" with no explanation. They're
   *      confused but the cart state is correct.
   *   B. Toast notification — "Couldn't remove right now. Try again?"
   *      Explicit but adds a UI element that doesn't exist anywhere else
   *      in the drawer. Need a toast component.
   *   C. Inline error in the bundle row — small red text under the
   *      Remove button: "Couldn't remove. Tap to retry."
   *      Fits the existing UI; clear next action.
   *   D. Auto-retry with backoff — try again after 1s, 3s, 7s before
   *      surfacing any error. Hides transient network blips entirely.
   *
   * Pick A/B/C/D (or hybrid) and implement below the marker.
   */
  function handleRemoveBundleFailure(bundleRow, parentEl, nextSibling, prevSubtotalText, prevCountText, subtotalEl, prevHeaderCounts) {
    /* --- Minimum viable rollback (always do this) --- */
    if (bundleRow && parentEl) {
      bundleRow.removeAttribute('data-ij-removing');
      bundleRow.style.transition = '';
      bundleRow.style.opacity = '';
      bundleRow.style.transform = '';
      bundleRow.style.maxHeight = '';
      bundleRow.style.marginTop = '';
      bundleRow.style.marginBottom = '';
      bundleRow.style.paddingTop = '';
      bundleRow.style.paddingBottom = '';
      var btn = bundleRow.querySelector('.ij-drawer-bundle-remove');
      if (btn) btn.classList.remove('ij-btn-loading');
      if (nextSibling && nextSibling.parentNode === parentEl) {
        parentEl.insertBefore(bundleRow, nextSibling);
      } else {
        parentEl.appendChild(bundleRow);
      }
    }
    if (subtotalEl && prevSubtotalText !== null) subtotalEl.textContent = prevSubtotalText;
    if (drawerCountEl && prevCountText !== null) drawerCountEl.textContent = prevCountText;
    /* Restore header cart-icon badge values (and visibility) we
       optimistically decremented before the failed API call. */
    if (Array.isArray(prevHeaderCounts)) {
      for (var i = 0; i < prevHeaderCounts.length; i++) {
        prevHeaderCounts[i].el.textContent = prevHeaderCounts[i].text;
        prevHeaderCounts[i].el.style.visibility = prevHeaderCounts[i].vis || '';
      }
    }

    /* --- TODO[user]: choose A / B / C / D and implement here --- */
  }

  /**
   * Sync the cart-icon badge on initial page load.
   *
   * Without this, the Liquid-rendered initial value (cart.item_count =
   * raw line items, e.g. "6" for one bundle) shows until the customer
   * opens the drawer and triggers our renderDrawer → updateCartCounts.
   *
   * Fetch /cart.js once at startup, compute the design count, push to
   * the badge. Silent failure — if cart.js is rate limited or down, we
   * just leave the Liquid initial value (worst case: matches what
   * Shopify thinks). Uses origFetch to skip CartJS's queue entirely.
   */
  function syncCartCountsOnLoad() {
    origFetch('/cart.js?_=' + Date.now(), { headers: { 'Accept': 'application/json' }, cache: 'no-store' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) { if (cart) updateCartCounts(cart); })
      .catch(function () { /* silent — keep Liquid value */ });
  }

  /**
   * Fetch /cart.js with timeout + transparent retry + visible error state.
   *
   * The OLD flow:
   *   - origFetch('/cart.js?_=...') → renderDrawer
   *   - On error: do nothing (left "Loading…" up forever)
   * Symptom: localhost dev server hiccups, drawer stuck on Loading… infinitely.
   * No way for the user to recover without page reload.
   *
   * NEW flow:
   *   1. 5-second timeout via AbortController. Slow networks AND cold dev
   *      proxies both get an upper bound on user pain.
   *   2. One silent retry on first failure — covers transient blips
   *      (Shopify rate limit, dev proxy reconnect, brief net hiccup).
   *   3. If retry also fails → render an explicit error state with a
   *      "Try again" button + "Open full cart page" escape hatch, so the
   *      user always has a way out.
   *
   * The timeout uses `AbortController` → guaranteed to fire even if the
   * underlying network stack hangs forever. Without this, fetch() can
   * sit in pending forever (some dev proxies + SW intercepts cause this).
   */
  var CART_FETCH_TIMEOUT_MS = 5000;
  function fetchCartWithTimeout() {
    var url = '/cart.js?_=' + Date.now();
    var ctrl = new AbortController();
    var timer = setTimeout(function () { ctrl.abort(); }, CART_FETCH_TIMEOUT_MS);
    return origFetch(url, {
      headers: { 'Accept': 'application/json' },
      cache: 'no-store',
      signal: ctrl.signal,
    }).then(function (r) {
      clearTimeout(timer);
      if (!r.ok) throw new Error('cart.js status ' + r.status);
      return r.json();
    }).catch(function (err) {
      clearTimeout(timer);
      throw err;
    });
  }

  function renderDrawerError(retryFn) {
    if (!drawerBodyEl) return;
    clearChildren(drawerBodyEl);
    var box = document.createElement('div');
    box.className = 'ij-drawer-empty';
    box.style.padding = '32px 20px';
    box.style.textAlign = 'center';

    var msg = document.createElement('p');
    msg.textContent = 'Couldn’t load your cart.';
    msg.style.cssText = 'margin: 0 0 8px; color: var(--ij-ink, #1a1a1a); font-family: var(--font-display, Georgia, serif); font-style: italic; font-size: 18px;';
    box.appendChild(msg);

    var sub = document.createElement('p');
    sub.textContent = 'Network or server issue. Try again, or open the full cart page.';
    sub.style.cssText = 'margin: 0 0 20px; color: var(--ij-ink-3, #707070); font-size: 13px;';
    box.appendChild(sub);

    var retry = document.createElement('button');
    retry.type = 'button';
    retry.textContent = 'Try again';
    retry.style.cssText = 'background: var(--ij-ink, #1a1a1a); color: #fff; border: 0; padding: 10px 22px; font: inherit; font-size: 12px; letter-spacing: 0.18em; text-transform: uppercase; cursor: pointer; border-radius: 999px; margin-right: 8px;';
    retry.addEventListener('click', function () {
      renderLoading();
      retryFn();
    });
    box.appendChild(retry);

    var openCart = document.createElement('a');
    openCart.href = '/cart';
    openCart.textContent = 'Open cart page';
    openCart.style.cssText = 'display: inline-block; padding: 10px 18px; font-size: 12px; letter-spacing: 0.12em; text-transform: uppercase; color: var(--ij-ink, #1a1a1a); text-decoration: underline; text-underline-offset: 4px;';
    box.appendChild(openCart);

    drawerBodyEl.appendChild(box);
    if (drawerFootEl) drawerFootEl.style.display = 'none';
  }

  function refreshDrawer() {
    mountDrawer();
    if (!drawerEl) return Promise.resolve();
    /* `cache: 'no-store'` + a cache-bust query param protects against the
       browser serving a stale /cart.js response from right before an
       add/change completed — the symptom is a drawer that opens stuck
       on "Loading…" while the server actually has the new items.
       The fetchCartWithTimeout helper adds a 5s timeout + transparent
       retry so transient failures don't strand the user. */
    return fetchCartWithTimeout()
      .then(renderDrawer)
      .catch(function (err) {
        console.warn('[ij] drawer refresh attempt 1 failed, retrying once', err);
        /* SILENT RETRY (after 600ms) — covers transient blips. */
        return new Promise(function (resolve) { setTimeout(resolve, 600); })
          .then(fetchCartWithTimeout)
          .then(renderDrawer)
          .catch(function (err2) {
            console.error('[ij] drawer refresh failed after retry', err2);
            /* VISIBLE ERROR STATE — user always has a way out. */
            renderDrawerError(refreshDrawer);
          });
      });
  }

  // ========================================================================
  // Header cart icon — open drawer instead of navigating
  // ========================================================================
  function interceptHeaderCart() {
    var link = document.querySelector('.nav-cart a[href*="/cart"]');
    if (!link || link.__ijHooked) return;
    link.__ijHooked = true;
    link.addEventListener('click', function (e) {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
      if (!$('.ij-cart-drawer')) return;
      e.preventDefault();
      openDrawer();
    });
  }

  window.addEventListener('ij:cart-added', function (e) {
    // /cart/add.js is already complete by the time our wrapper fires this event,
    // so Shopify's internal cart state is up to date. Open immediately; the
    // loading placeholder covers the fraction of a second until /cart.js returns.
    openDrawer();
    handleEditMarker(e && e.detail && e.detail.data);
  });

  window.addEventListener('ij:cart-mutated', function () {
    /* Drawer refresh handles the icon update via renderDrawer →
       updateCartCounts. If drawer is closed, we still need to sync
       the icon to truth — fetch /cart.js silently in that case. */
    if (drawerEl && drawerEl.classList.contains('is-open')) {
      refreshDrawer();
    } else {
      origFetch('/cart.js?_=' + Date.now(), { headers: { 'Accept': 'application/json' }, cache: 'no-store' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) { if (cart) updateCartCounts(cart); })
        .catch(function () { /* silent */ });
    }
  });

  // Charm builder (and any other script) can request the drawer opens by
  // firing `ij:cart:added` on window. Fires after the React bundle's
  // successful /cart/add.js POST — gives the customer an immediate "your
  // piece is in the cart" affordance without them hunting for the cart icon.
  window.addEventListener('ij:cart:added', function () {
    openDrawer();
    /* Use the design-aware count (bundles + loners). Don't write
       cart.item_count directly — that would show "6" for a single
       designed piece, which is what triggered the user complaint. */
    origFetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
      updateCartCounts(cart);
    }).catch(function () { /* ignore */ });
  });

  /* v0.9.196: the header lives inside #ij-spa-region, so a soft navigation swaps in a fresh header —
     a new cart icon that is not hooked (it navigated to /cart instead of opening the drawer) and a
     server-rendered raw count ("0" showing on an empty bag). Re-hook and re-apply the design count.
     No network: reuse the last cart snapshot. The drawer itself sits outside the region and persists. */
  document.addEventListener('ij:navigated', function () {
    interceptHeaderCart();
    wireCartButtons();
    if (lastCart) updateCartCounts(lastCart);
    else syncCartCountsOnLoad();
  });

  /* WP-B: Back/Forward from the back-forward cache restores the page with a frozen badge and
     drawer (the cart may have changed on the page we came from). Re-read the cart. */
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    if (drawerEl && drawerEl.classList.contains('is-open')) refreshDrawer();
    else syncCartCountsOnLoad();
  });

  function init() {
    mountDrawer();
    interceptHeaderCart();
    wireCartButtons();
    /* Initial sync: the Liquid-rendered cart icon shows raw line-item
       count (e.g. "6" for one bundle of a chain + 5 add-ons). Fetch
       once on load and rewrite to design count so the customer never
       sees the misleading raw number. */
    syncCartCountsOnLoad();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.IJ = {
    __ready: true,
    openDrawer: openDrawer,
    closeDrawer: closeDrawer,
    refreshDrawer: refreshDrawer,
    /* WP-B: the one cart count (see cartItemCount) + a way for templates/cart.liquid to push it. */
    cartItemCount: cartItemCount,
    syncCartCounts: function (cart) { if (cart) updateCartCounts(cart); else syncCartCountsOnLoad(); },
    progress: { start: progressStart, stop: progressStop },
  };
})();
